import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  Heading,
  VStack,
  HStack,
  Spinner,
  Center,
  useToast,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Divider
} from '@chakra-ui/react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import { getDashboardStats } from '../../services/apiService'
import { useRecoilValue } from 'recoil'
import { authState } from '../../atoms/auth'

interface DashboardStats {
  // 사용자 통계
  totalUsers: number
  newUsersToday: number
  newUsersThisWeek: number
  newUsersThisMonth: number
  userGrowthRate: {
    daily: number
    weekly: number
    monthly: number
  }
  
  // 활성 사용자
  activeBots: number
  basicUsers: number
  vipUsers: number
  secretUsers: number
  normalUsers: number
  
  // 사용자 증가 추이 (최근 30일)
  userGrowthTrend: Array<{
    date: string
    totalUsers: number
    newUsers: number
  }>
  
  // 등급별 분포
  userGradeDistribution: Array<{
    grade: string
    count: number
    percentage: number
  }>
  
  // 심볼별 활성 봇 통계
  symbolBotStats: Array<{
    symbol: string
    activeBots: number
    totalBots: number
    percentage: number
  }>
  
  // 사용자별 이용권 매출 보상 순위
  userRewardRanking: Array<{
    userId: number
    account: string
    username: string
    totalRewardUsdt: number
    totalRewardPoints: number
    rewardCount: number
    lastRewardDate: string
  }>
  
  // 심볼별 일일 신호 통계
  dailySignalStats: Array<{
    symbol: string
    buyCount: number
    sellCount: number
    totalCount: number
    buyPercentage: number
    sellPercentage: number
  }>
  
  // 커뮤니티 일일 통계
  dailyCommunityStats: {
    profitPosts: number
    freePosts: number
    totalPosts: number
    profitPercentage: number
    freePercentage: number
  }
  
  // 커뮤니티 좋아요 일일 통계
  dailyLikeStats: {
    totalLikes: number
    likeCount: number
    dislikeCount: number
    likePercentage: number
    dislikePercentage: number
  }
  
  // 매출 현황 통계
  salesStats: {
    dailySales: Array<{
      date: string
      amount: number
    }>
    weeklySales: Array<{
      week: string
      amount: number
    }>
    monthlySales: Array<{
      month: string
      amount: number
    }>
    totalSales: number
    todaySales: number
    thisWeekSales: number
    thisMonthSales: number
  }
}

const Dashboard = () => {
  const auth = useRecoilValue(authState)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const toast = useToast()

  // XrpKorea 계정인 경우 하드코딩된 값
  const isDemoMode = auth.account === 'XrpKorea'
  const demoStats: DashboardStats = {
    totalUsers: 4135,
    newUsersToday: 12,
    newUsersThisWeek: 89,
    newUsersThisMonth: 342,
    userGrowthRate: {
      daily: 2.3,
      weekly: 5.7,
      monthly: 9.1
    },
    activeBots: 3247,
    basicUsers: 2892,
    vipUsers: 126,
    secretUsers: 682,
    normalUsers: 435,
    userGrowthTrend: [],
    userGradeDistribution: [],
    symbolBotStats: [],
    userRewardRanking: [],
    dailySignalStats: [],
    dailyCommunityStats: {
      profitPosts: 0,
      freePosts: 0,
      totalPosts: 0,
      profitPercentage: 0,
      freePercentage: 0
    },
    dailyLikeStats: {
      totalLikes: 0,
      likeCount: 0,
      dislikeCount: 0,
      likePercentage: 0,
      dislikePercentage: 0
    },
    salesStats: {
      dailySales: [],
      weeklySales: [],
      monthlySales: [],
      totalSales: 0,
      todaySales: 0,
      thisWeekSales: 0,
      thisMonthSales: 0
    }
  }

  useEffect(() => {
    if (!isDemoMode) {
      fetchDashboardStats()
    } else {
      setStats(demoStats)
      setLoading(false)
    }
  }, [])

  const fetchDashboardStats = async () => {
    try {
      setLoading(true)
      const { data: res } = await getDashboardStats()
      if (res.statusCode === 200) {
        setStats(res.data)
      }
    } catch (error: any) {
      toast({
        title: '데이터 로딩 실패',
        description: error.response?.data?.message || '알 수 없는 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

  // 표시할 통계 데이터 (데모 모드면 하드코딩 값, 아니면 실제 DB 값)
  const displayStats = isDemoMode ? demoStats : stats

  if (loading && !isDemoMode) {
    return (
      <DefaultLayout>
        <AdminNav />
        <Center h="50vh">
          <Spinner size="xl" color="blue.500" />
        </Center>
      </DefaultLayout>
    )
  }

  return (
    <DefaultLayout>
      <AdminNav />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* 페이지 제목 */}
          <Box>
            <Heading size="lg" mb={2}>관리자 대시보드</Heading>
            <Text color="gray.600">플랫폼 운영 현황을 한눈에 확인하세요</Text>
          </Box>

          {/* 핵심 지표 카드 */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 6 }} spacing={6}>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>총 사용자 수</StatLabel>
                  <StatNumber fontSize="2xl">{displayStats?.totalUsers?.toLocaleString() || 0}</StatNumber>
                  <StatHelpText>
                    <StatArrow type="increase" />
                    오늘 +{displayStats?.newUsersToday || 0}명
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>활성 봇 수</StatLabel>
                  <StatNumber fontSize="2xl">{displayStats?.activeBots || 0}</StatNumber>
                  <StatHelpText>현재 실행 중인 봇</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>NORMAL 사용자</StatLabel>
                  <StatNumber fontSize="2xl">{displayStats?.normalUsers || 0}</StatNumber>
                  <StatHelpText>일반 무료 사용자</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>BASIC 사용자</StatLabel>
                  <StatNumber fontSize="2xl">{displayStats?.basicUsers || 0}</StatNumber>
                  <StatHelpText>BASIC 등급 사용자</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>VIP 사용자</StatLabel>
                  <StatNumber fontSize="2xl">{displayStats?.vipUsers || 0}</StatNumber>
                  <StatHelpText>VIP 등급 활성 사용자</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>SECRET 사용자</StatLabel>
                  <StatNumber fontSize="2xl">{displayStats?.secretUsers || 0}</StatNumber>
                  <StatHelpText>SECRET 등급 활성 사용자</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* 사용자 증가 추이 차트 */}
          <Card>
            <CardHeader>
              <Heading size="md">사용자 증가 추이 (최근 30일)</Heading>
            </CardHeader>
            <CardBody>
              <Box h="400px">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats?.userGrowthTrend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="totalUsers" 
                      stackId="1"
                      stroke="#8884d8" 
                      fill="#8884d8" 
                      name="총 사용자"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="newUsers" 
                      stackId="2"
                      stroke="#82ca9d" 
                      fill="#82ca9d" 
                      name="신규 사용자"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardBody>
          </Card>

          {/* 사용자 증감률 */}
          <Card>
            <CardHeader>
              <Heading size="md">사용자 증감률</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Stat>
                  <StatLabel>일일 증감률</StatLabel>
                  <StatNumber color={stats?.userGrowthRate?.daily && stats.userGrowthRate.daily > 0 ? 'green.500' : 'red.500'}>
                    {stats?.userGrowthRate?.daily ? `${stats.userGrowthRate.daily > 0 ? '+' : ''}${stats.userGrowthRate.daily.toFixed(1)}%` : '0%'}
                  </StatNumber>
                  <StatHelpText>
                    <StatArrow type={stats?.userGrowthRate?.daily && stats.userGrowthRate.daily > 0 ? 'increase' : 'decrease'} />
                    전일 대비
                  </StatHelpText>
                </Stat>

                <Divider />

                <Stat>
                  <StatLabel>주간 증감률</StatLabel>
                  <StatNumber color={stats?.userGrowthRate?.weekly && stats.userGrowthRate.weekly > 0 ? 'green.500' : 'red.500'}>
                    {stats?.userGrowthRate?.weekly ? `${stats.userGrowthRate.weekly > 0 ? '+' : ''}${stats.userGrowthRate.weekly.toFixed(1)}%` : '0%'}
                  </StatNumber>
                  <StatHelpText>
                    <StatArrow type={stats?.userGrowthRate?.weekly && stats.userGrowthRate.weekly > 0 ? 'increase' : 'decrease'} />
                    전주 대비
                  </StatHelpText>
                </Stat>

                <Divider />

                <Stat>
                  <StatLabel>월간 증감률</StatLabel>
                  <StatNumber color={stats?.userGrowthRate?.monthly && stats.userGrowthRate.monthly > 0 ? 'green.500' : 'red.500'}>
                    {stats?.userGrowthRate?.monthly ? `${stats.userGrowthRate.monthly > 0 ? '+' : ''}${stats.userGrowthRate.monthly.toFixed(1)}%` : '0%'}
                  </StatNumber>
                  <StatHelpText>
                    <StatArrow type={stats?.userGrowthRate?.monthly && stats.userGrowthRate.monthly > 0 ? 'increase' : 'decrease'} />
                    전월 대비
                  </StatHelpText>
                </Stat>
              </VStack>
            </CardBody>
          </Card>

          {/* 등급별 사용자 분포 및 심볼별 봇 통계 */}
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
            <Card>
              <CardHeader>
                <Heading size="md">등급별 사용자 분포</Heading>
              </CardHeader>
              <CardBody>
                <Box h="300px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats?.userGradeDistribution || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry: any) => `${entry.grade} ${entry.percentage.toFixed(1)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {(stats?.userGradeDistribution || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <Heading size="md">심볼별 활성 봇 통계</Heading>
              </CardHeader>
              <CardBody>
                <Box h="300px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats?.symbolBotStats || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="symbol" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="activeBots" fill="#8884d8" name="활성 봇" />
                      <Bar dataKey="totalBots" fill="#82ca9d" name="전체 봇" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardBody>
            </Card>
          </Grid>

          {/* 심볼별 봇 상세 통계 테이블 */}
          <Card>
            <CardHeader>
              <Heading size="md">심볼별 봇 상세 통계</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {(stats?.symbolBotStats || []).map((symbolStat, index) => (
                  <Box
                    key={symbolStat.symbol}
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    bg="gray.50"
                  >
                    <VStack spacing={2} align="stretch">
                      <Text fontSize="lg" fontWeight="bold" color="blue.600">
                        {symbolStat.symbol}
                      </Text>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">활성 봇:</Text>
                        <Text fontSize="lg" fontWeight="bold" color="green.600">
                          {symbolStat.activeBots}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">전체 봇:</Text>
                        <Text fontSize="lg" fontWeight="bold">
                          {symbolStat.totalBots}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">활성률:</Text>
                        <Text fontSize="lg" fontWeight="bold" color="purple.600">
                          {symbolStat.percentage.toFixed(1)}%
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* 매출 현황 통계 */}
          <Card>
            <CardHeader>
              <Heading size="md">매출 현황 통계</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
                <Box p={4} borderWidth="1px" borderRadius="lg" bg="green.50">
                  <VStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">오늘 매출</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {Math.round(stats?.salesStats?.todaySales || 0).toLocaleString()} USDT
                    </Text>
                  </VStack>
                </Box>
                <Box p={4} borderWidth="1px" borderRadius="lg" bg="blue.50">
                  <VStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">이번 주 매출</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                      {Math.round(stats?.salesStats?.thisWeekSales || 0).toLocaleString()} USDT
                    </Text>
                  </VStack>
                </Box>
                <Box p={4} borderWidth="1px" borderRadius="lg" bg="purple.50">
                  <VStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">이번 달 매출</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                      {Math.round(stats?.salesStats?.thisMonthSales || 0).toLocaleString()} USDT
                    </Text>
                  </VStack>
                </Box>
                <Box p={4} borderWidth="1px" borderRadius="lg" bg="orange.50">
                  <VStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">총 매출</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                      {Math.round(stats?.salesStats?.totalSales || 0).toLocaleString()} USDT
                    </Text>
                  </VStack>
                </Box>
              </SimpleGrid>

              <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr 1fr' }} gap={6}>
                <Box>
                  <Heading size="sm" mb={4}>일별 매출 추이 (최근 30일)</Heading>
                  <Box h="300px">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stats?.salesStats?.dailySales || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip formatter={(value) => [`${Math.round(Number(value))} USDT`, '매출']} />
                        <Legend />
                        <Line type="monotone" dataKey="amount" stroke="#8884d8" name="일별 매출" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                <Box>
                  <Heading size="sm" mb={4}>주별 매출 추이 (최근 12주)</Heading>
                  <Box h="300px">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats?.salesStats?.weeklySales || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="week" />
                        <YAxis />
                        <Tooltip formatter={(value) => [`${Math.round(Number(value))} USDT`, '매출']} />
                        <Legend />
                        <Bar dataKey="amount" fill="#82ca9d" name="주별 매출" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                <Box>
                  <Heading size="sm" mb={4}>월별 매출 추이 (최근 12개월)</Heading>
                  <Box h="300px">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats?.salesStats?.monthlySales || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => [`${Math.round(Number(value))} USDT`, '매출']} />
                        <Legend />
                        <Area type="monotone" dataKey="amount" stroke="#ffc658" fill="#ffc658" name="월별 매출" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Grid>
            </CardBody>
          </Card>

          {/* 커뮤니티 일일 통계 */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Card>
              <CardHeader>
                <Heading size="md">커뮤니티 일일 게시글 통계</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold" color="green.600">PROFIT 게시글</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {stats?.dailyCommunityStats?.profitPosts || 0}개
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold" color="blue.600">FREE 게시글</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                      {stats?.dailyCommunityStats?.freePosts || 0}개
                    </Text>
                  </HStack>
                  <Divider />
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold">총 게시글</Text>
                    <Text fontSize="2xl" fontWeight="bold">
                      {stats?.dailyCommunityStats?.totalPosts || 0}개
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">PROFIT 비율</Text>
                    <Text fontSize="lg" fontWeight="bold" color="green.600">
                      {stats?.dailyCommunityStats?.profitPercentage?.toFixed(1) || 0}%
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">FREE 비율</Text>
                    <Text fontSize="lg" fontWeight="bold" color="blue.600">
                      {stats?.dailyCommunityStats?.freePercentage?.toFixed(1) || 0}%
                    </Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <Heading size="md">커뮤니티 일일 좋아요 통계</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold" color="green.600">좋아요</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {stats?.dailyLikeStats?.likeCount || 0}개
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold" color="red.600">싫어요</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="red.600">
                      {stats?.dailyLikeStats?.dislikeCount || 0}개
                    </Text>
                  </HStack>
                  <Divider />
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold">총 반응</Text>
                    <Text fontSize="2xl" fontWeight="bold">
                      {stats?.dailyLikeStats?.totalLikes || 0}개
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">좋아요 비율</Text>
                    <Text fontSize="lg" fontWeight="bold" color="green.600">
                      {stats?.dailyLikeStats?.likePercentage?.toFixed(1) || 0}%
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">싫어요 비율</Text>
                    <Text fontSize="lg" fontWeight="bold" color="red.600">
                      {stats?.dailyLikeStats?.dislikePercentage?.toFixed(1) || 0}%
                    </Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* 심볼별 일일 신호 통계 */}
          <Card>
            <CardHeader>
              <Heading size="md">심볼별 일일 신호 통계</Heading>
            </CardHeader>
            <CardBody>
              <Box h="400px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats?.dailySignalStats || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="symbol" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="buyCount" fill="#00C49F" name="Buy 신호" />
                    <Bar dataKey="sellCount" fill="#FF8042" name="Sell 신호" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardBody>
          </Card>

          {/* 심볼별 신호 상세 통계 */}
          <Card>
            <CardHeader>
              <Heading size="md">심볼별 신호 상세 통계</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {(stats?.dailySignalStats || []).map((signalStat, index) => (
                  <Box
                    key={signalStat.symbol}
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    bg="gray.50"
                  >
                    <VStack spacing={2} align="stretch">
                      <Text fontSize="lg" fontWeight="bold" color="blue.600">
                        {signalStat.symbol}
                      </Text>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Buy 신호:</Text>
                        <Text fontSize="lg" fontWeight="bold" color="green.600">
                          {signalStat.buyCount}건
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Sell 신호:</Text>
                        <Text fontSize="lg" fontWeight="bold" color="red.600">
                          {signalStat.sellCount}건
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">총 신호:</Text>
                        <Text fontSize="lg" fontWeight="bold">
                          {signalStat.totalCount}건
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Buy 비율:</Text>
                        <Text fontSize="lg" fontWeight="bold" color="green.600">
                          {signalStat.buyPercentage.toFixed(1)}%
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Sell 비율:</Text>
                        <Text fontSize="lg" fontWeight="bold" color="red.600">
                          {signalStat.sellPercentage.toFixed(1)}%
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* 사용자별 이용권 매출 보상 순위 */}
          <Card>
            <CardHeader>
              <Heading size="md">사용자별 이용권 매출 보상 순위</Heading>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Box as="table" width="100%" sx={{ borderCollapse: 'collapse' }}>
                  <Box as="thead">
                    <Box as="tr" borderBottom="1px" borderColor="gray.200">
                      <Box as="th" p={3} textAlign="left" fontWeight="bold" color="gray.700">순위</Box>
                      <Box as="th" p={3} textAlign="left" fontWeight="bold" color="gray.700">사용자</Box>
                      <Box as="th" p={3} textAlign="right" fontWeight="bold" color="gray.700">총 보상 USDT</Box>
                      <Box as="th" p={3} textAlign="right" fontWeight="bold" color="gray.700">총 보상 포인트</Box>
                      <Box as="th" p={3} textAlign="center" fontWeight="bold" color="gray.700">보상 횟수</Box>
                      <Box as="th" p={3} textAlign="left" fontWeight="bold" color="gray.700">최근 보상일</Box>
                    </Box>
                  </Box>
                  <Box as="tbody">
                    {(stats?.userRewardRanking || []).map((user, index) => (
                      <Box as="tr" key={user.userId} borderBottom="1px" borderColor="gray.100" _hover={{ bg: "gray.50" }}>
                        <Box as="td" p={3}>
                          <Text fontWeight="bold" color={index < 3 ? "gold" : "gray.600"}>
                            {index + 1}위
                          </Text>
                        </Box>
                        <Box as="td" p={3}>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="bold" color="blue.600">
                              {user.username || user.account}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {user.account}
                            </Text>
                          </VStack>
                        </Box>
                        <Box as="td" p={3} textAlign="right">
                          <Text fontWeight="bold" color="green.600">
                            {user.totalRewardUsdt.toLocaleString()} USDT
                          </Text>
                        </Box>
                        <Box as="td" p={3} textAlign="right">
                          <Text fontWeight="bold" color="purple.600">
                            {user.totalRewardPoints.toLocaleString()} P
                          </Text>
                        </Box>
                        <Box as="td" p={3} textAlign="center">
                          <Text fontWeight="bold" color="orange.600">
                            {user.rewardCount}회
                          </Text>
                        </Box>
                        <Box as="td" p={3}>
                          <Text fontSize="sm" color="gray.600">
                            {new Date(user.lastRewardDate).toLocaleDateString('ko-KR')}
                          </Text>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default Dashboard